/*!
 * College Classics — Shopify theme scripts
 * Vanilla JS, no build step. Feature map:
 *   1. utilities            5. cart + promo code (ONE code at a time)
 *   2. scroll lock/drawers  6. wishlist (localStorage)
 *   3. header / menu        7. search, collection filters, product page
 *   4. small widgets        8. scroll animations (AOS)
 * Everything that is clicked is wired through delegated listeners, so sections that are re-rendered
 * (theme editor, cart drawer refresh, filtering) keep working without re-binding.
 */
(() => {
	'use strict';

	/* ==========================================================================
     1. Utilities
     ========================================================================== */
	const doc = document;
	const $ = (sel, root = doc) => root.querySelector(sel);
	const $$ = (sel, root = doc) => Array.from(root.querySelectorAll(sel));
	const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
	const ready = fn =>
		doc.readyState === 'loading'
			? doc.addEventListener('DOMContentLoaded', fn)
			: fn();
	const escapeHtml = s =>
		String(s == null ? '' : s).replace(
			/[&<>"']/g,
			c =>
				({
					'&': '&amp;',
					'<': '&lt;',
					'>': '&gt;',
					'"': '&quot;',
					"'": '&#39;',
				})[c],
		);
	const debounce = (fn, ms) => {
		let t;
		return (...args) => {
			clearTimeout(t);
			t = setTimeout(() => fn(...args), ms);
		};
	};
	const emit = (name, detail) =>
		doc.dispatchEvent(new CustomEvent(name, { detail }));
	const reducedMotion = () =>
		window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	const store = {
		get(key, fallback) {
			try {
				const raw = localStorage.getItem(key);
				return raw == null ? fallback : JSON.parse(raw);
			} catch (e) {
				return fallback;
			}
		},
		set(key, value) {
			try {
				localStorage.setItem(key, JSON.stringify(value));
			} catch (e) {
				/* private mode / quota: the feature simply doesn't persist */
			}
		},
		del(key) {
			try {
				localStorage.removeItem(key);
			} catch (e) {
				/* ignore */
			}
		},
	};

	let config = {};
	try {
		config = JSON.parse(($('#theme-config') || {}).textContent || '{}');
	} catch (e) {
		config = {};
	}
	const routes = config.routes || {};
	// Shopify's `t` filter HTML-escapes translations (apostrophes -> &#39; etc.) before they reach the
	// `| json` here, so a string like "isn't" arrives as the literal text "isn&#39;t" - decode it back.
	const decodeEntities = str => {
		if (typeof str !== 'string' || str.indexOf('&') === -1) return str;
		const el = doc.createElement('textarea');
		el.innerHTML = str;
		return el.value;
	};
	const S = {};
	Object.keys(config.strings || {}).forEach(key => {
		S[key] = decodeEntities(config.strings[key]);
	});
	const jsonUrl = base => `${base}.js`;

	/* ---- money -------------------------------------------------------------- */
	function formatMoney(cents, format) {
		const fmt = format || config.moneyFormat || '${{amount}}';
		const value =
			typeof cents === 'string'
				? Number(cents.replace('.', ''))
				: Number(cents);
		const withDelimiters = (
			number,
			precision = 2,
			thousands = ',',
			decimal = '.',
		) => {
			if (Number.isNaN(number) || number == null) return '0';
			const fixed = (number / 100).toFixed(precision).split('.');
			const dollars = fixed[0].replace(
				/(\d)(?=(\d\d\d)+(?!\d))/g,
				`$1${thousands}`,
			);
			return fixed[1] ? dollars + decimal + fixed[1] : dollars;
		};
		const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
		let out = '';
		switch (match && match[1]) {
			case 'amount_no_decimals':
				out = withDelimiters(value, 0);
				break;
			case 'amount_with_comma_separator':
				out = withDelimiters(value, 2, '.', ',');
				break;
			case 'amount_no_decimals_with_comma_separator':
				out = withDelimiters(value, 0, '.', ',');
				break;
			case 'amount_no_decimals_with_space_separator':
				out = withDelimiters(value, 0, ' ');
				break;
			case 'amount_with_space_separator':
				out = withDelimiters(value, 2, ' ', ',');
				break;
			case 'amount_with_apostrophe_separator':
				out = withDelimiters(value, 2, "'");
				break;
			default:
				out = withDelimiters(value, 2);
		}
		return fmt.replace(/\{\{\s*\w+\s*\}\}/, out);
	}
	/** Same as {{ price | money_without_trailing_zeros }} */
	function formatMoneyShort(cents) {
		if (Number(cents) % 100 !== 0) return formatMoney(cents);
		const fmt = (config.moneyFormat || '${{amount}}')
			.replace('{{amount}}', '{{amount_no_decimals}}')
			.replace(
				'{{amount_with_comma_separator}}',
				'{{amount_no_decimals_with_comma_separator}}',
			)
			.replace('{{ amount }}', '{{amount_no_decimals}}');
		return formatMoney(cents, fmt);
	}

	/* ---- network ------------------------------------------------------------ */
	async function postJSON(url, body) {
		const res = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
				'X-Requested-With': 'XMLHttpRequest',
			},
			body: JSON.stringify(body),
		});
		let data = null;
		try {
			data = await res.json();
		} catch (e) {
			data = null;
		}
		if (!res.ok || (data && data.status && data.status >= 400)) {
			const err = new Error(
				(data && (data.description || data.message)) || S.cartError || 'Error',
			);
			err.data = data;
			throw err;
		}
		return data;
	}
	const parseHTML = html => new DOMParser().parseFromString(html, 'text/html');
	/** Single place for full-page navigations (also makes the flows testable). */
	const Nav = {
		go(url) {
			window.location.href = url;
		},
	};
	const formatCountText = (n, one, other) =>
		n === 1 ? one : (other || one).replace(/\d+/, n);

	/* ==========================================================================
     2. Scroll lock + drawers
     ========================================================================== */
	let lockCount = 0;
	function lockScroll() {
		if (lockCount++ > 0) return;
		const y = window.scrollY;
		const scrollbar = window.innerWidth - doc.body.offsetWidth;
		doc.documentElement.style.scrollBehavior = 'auto';
		doc.body.style.paddingRight = `${scrollbar}px`;
		doc.body.classList.add('dis-scroll');
		doc.body.dataset.position = y;
		doc.body.style.top = `-${y}px`;
	}
	function unlockScroll() {
		if (--lockCount > 0) return;
		lockCount = 0;
		const y = parseInt(doc.body.dataset.position || '0', 10);
		doc.body.style.paddingRight = '';
		doc.body.style.top = '';
		doc.body.classList.remove('dis-scroll');
		delete doc.body.dataset.position;
		window.scrollTo(0, y);
		requestAnimationFrame(() => {
			doc.documentElement.style.scrollBehavior = '';
		});
	}

	const FOCUSABLE =
		'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
	function trapFocus(container, e) {
		if (e.key !== 'Tab') return;
		const items = $$(FOCUSABLE, container).filter(
			el => el.offsetParent !== null || el === doc.activeElement,
		);
		if (!items.length) return;
		const first = items[0];
		const last = items[items.length - 1];
		if (e.shiftKey && doc.activeElement === first) {
			e.preventDefault();
			last.focus();
		} else if (!e.shiftKey && doc.activeElement === last) {
			e.preventDefault();
			first.focus();
		}
	}

	const Drawers = {
		current: null,
		trigger: null,
		open(el, trigger) {
			if (!el) return;
			if (this.current && this.current !== el)
				this.close(this.current, { restoreScroll: false, restoreFocus: false });
			else if (!this.current) lockScroll();
			this.trigger = trigger || this.trigger;
			el.classList.add('is-open');
			el.setAttribute('aria-hidden', 'false');
			this.current = el;
			emit('drawer:open', { id: el.id, el });
			window.setTimeout(() => {
				if (this.current !== el) return;
				const focusTarget =
					$('[data-search-input]', el) || $('.drawer__close', el);
				if (focusTarget) focusTarget.focus({ preventScroll: true });
			}, 320);
		},
		close(el, { restoreScroll = true, restoreFocus = true } = {}) {
			if (!el) return;
			el.classList.remove('is-open');
			el.setAttribute('aria-hidden', 'true');
			if (this.current === el) {
				this.current = null;
				if (restoreScroll) unlockScroll();
				if (restoreFocus && this.trigger && doc.contains(this.trigger))
					this.trigger.focus({ preventScroll: true });
			}
			emit('drawer:close', { id: el.id, el });
		},
		byId(id) {
			return id ? doc.getElementById(id) : null;
		},
	};

	doc.addEventListener('click', e => {
		const opener = e.target.closest('[data-drawer-open]');
		if (opener) {
			const target = Drawers.byId(opener.dataset.drawerOpen);
			if (target) {
				e.preventDefault();
				Drawers.open(target, opener);
			}
			return;
		}
		const closer = e.target.closest('[data-drawer-close]');
		if (closer) {
			const drawer = closer.closest('.drawer');
			if (drawer) Drawers.close(drawer);
		}
	});
	doc.addEventListener('keydown', e => {
		if (!Drawers.current) return;
		if (e.key === 'Escape') Drawers.close(Drawers.current);
		else trapFocus($('.drawer__panel', Drawers.current) || Drawers.current, e);
	});

	/* ==========================================================================
     3. Header, mobile menu, announcement marquee
     ========================================================================== */
	function initHeader() {
		const header = $('.header');
		if (!header) return;
		const setHeight = () =>
			doc.documentElement.style.setProperty(
				'--header-height',
				`${header.offsetHeight}px`,
			);
		setHeight();
		if (!window.__ccHeaderBound) {
			window.__ccHeaderBound = true;
			window.addEventListener(
				'resize',
				debounce(() => {
					const h = $('.header');
					if (h)
						doc.documentElement.style.setProperty(
							'--header-height',
							`${h.offsetHeight}px`,
						);
				}, 100),
			);
			let ticking = false;
			const update = () => {
				ticking = false;
				const h = $('.header');
				if (!h || doc.body.classList.contains('dis-scroll')) return;
				h.classList.toggle(
					'is-stuck',
					window.scrollY > 0 && h.getBoundingClientRect().top <= 0,
				);
			};
			window.addEventListener(
				'scroll',
				() => {
					if (ticking) return;
					ticking = true;
					requestAnimationFrame(update);
				},
				{ passive: true },
			);
			update();
		}
	}

	function initMarquee() {
		const track = $('[data-marquee-track]');
		const list = track && $('[data-marquee-list]', track);
		if (!track || !list) return;
		const mq = window.matchMedia('(max-width: 1024px)');
		let clones = [];
		const build = () => {
			clones.forEach(c => c.remove());
			clones = [];
			track.style.removeProperty('--marquee-shift');
			track.style.removeProperty('animation-duration');
			if (!mq.matches) return;
			const gap = parseFloat(getComputedStyle(list).marginRight) || 0;
			const width = list.getBoundingClientRect().width + gap;
			if (!width) return;
			const copies = Math.ceil(window.innerWidth / width) + 1;
			for (let i = 1; i < copies; i += 1) {
				const clone = list.cloneNode(true);
				clone.removeAttribute('data-marquee-list');
				clone.classList.add('features-message__list--clone');
				clone.setAttribute('aria-hidden', 'true');
				$$('img', clone).forEach(img => img.setAttribute('alt', ''));
				track.appendChild(clone);
				clones.push(clone);
			}
			track.style.setProperty('--marquee-shift', `${width}px`);
			track.style.animationDuration = `${width / 45}s`;
		};
		let timer = null;
		if (!track.dataset.marqueeBound) {
			track.dataset.marqueeBound = 'true';
			window.addEventListener('resize', () => {
				clearTimeout(timer);
				timer = setTimeout(build, 150);
			});
		}
		build();
		if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(build);
	}

	const MobileMenu = {
		timer: null,
		isOpen() {
			const menu = $('#mobile-menu');
			return !!menu && menu.classList.contains('is-open');
		},
		open() {
			const menu = $('#mobile-menu');
			const burger = $('[data-menu-open]');
			if (!menu || this.isOpen()) return;
			const bar = $('.features-message');
			const offset = bar
				? Math.max(0, Math.round(bar.getBoundingClientRect().bottom))
				: 0;
			menu.style.top = `${offset}px`;
			lockScroll();
			window.clearTimeout(this.timer);
			menu.hidden = false;
			void menu.offsetWidth;
			menu.classList.add('is-open');
			menu.setAttribute('aria-hidden', 'false');
			if (burger) burger.setAttribute('aria-expanded', 'true');
			const close = $('[data-menu-close]', menu);
			if (close) close.focus({ preventScroll: true });
		},
		close({ restoreFocus = true } = {}) {
			const menu = $('#mobile-menu');
			const burger = $('[data-menu-open]');
			if (!menu || !this.isOpen()) return;
			menu.classList.remove('is-open');
			menu.setAttribute('aria-hidden', 'true');
			if (burger) burger.setAttribute('aria-expanded', 'false');
			this.timer = window.setTimeout(() => {
				menu.hidden = true;
			}, 250);
			unlockScroll();
			if (restoreFocus && burger) burger.focus({ preventScroll: true });
		},
	};
	doc.addEventListener('click', e => {
		if (e.target.closest('[data-menu-open]')) MobileMenu.open();
		else if (e.target.closest('[data-menu-close]')) MobileMenu.close();
		else if (e.target.closest('.mobile-menu__link'))
			MobileMenu.close({ restoreFocus: false });
	});
	doc.addEventListener('keydown', e => {
		if (!MobileMenu.isOpen()) return;
		if (e.key === 'Escape') MobileMenu.close();
		else trapFocus($('#mobile-menu'), e);
	});
	window.matchMedia('(max-width: 768px)').addEventListener('change', e => {
		if (!e.matches) MobileMenu.close({ restoreFocus: false });
	});

	/* ==========================================================================
     4. Small widgets: accordion, unit toggle, contact topics, carousel, thumbs, video…
     ========================================================================== */
	// Accordion (product page, FAQ)
	doc.addEventListener('click', e => {
		const question = e.target.closest('.accordion__question');
		if (!question) return;
		const item = question.closest('.accordion__item');
		if (!item) return;
		const open = item.classList.toggle('is-open');
		question.setAttribute('aria-expanded', String(open));
	});

	// Size guide: inches / cm
	doc.addEventListener('click', e => {
		const btn = e.target.closest('[data-unit-btn]');
		if (!btn) return;
		const wrap = btn.closest('.size-guide__table-wrap') || doc;
		const unit = btn.dataset.unitBtn;
		$$('[data-unit-btn]', wrap).forEach(b =>
			b.classList.toggle('is-active', b === btn),
		);
		$$('[data-in]', wrap).forEach(cell => {
			cell.textContent = unit === 'cm' ? cell.dataset.cm : cell.dataset.in;
		});
		$$('[data-unit-label]', wrap).forEach(label => {
			label.textContent = `${label.dataset.unitLabel} (${unit})`;
		});
	});

	// Contact form topics
	doc.addEventListener('click', e => {
		const btn = e.target.closest('.contact-form__type[data-type]');
		if (!btn) return;
		const form = btn.closest('form');
		if (!form) return;
		const type = btn.dataset.type;
		$$('.contact-form__type', form).forEach(b => {
			const active = b === btn;
			b.classList.toggle('is-active', active);
			b.setAttribute('aria-pressed', String(active));
		});
		$$('[data-extra]', form).forEach(extra => {
			const match = extra.dataset.extra === type;
			extra.hidden = !match;
			$$('input, textarea, select', extra).forEach(field => {
				field.disabled = !match;
			});
		});
		const topic = $('[data-contact-topic]', form);
		if (topic) topic.value = btn.dataset.topicName || btn.textContent.trim();
	});

	// Deep link: /pages/contact?topic=custom-team-orders pre-selects that topic tab (footer link, action cards…)
	function initContactTopicFromURL() {
		const wanted = new URLSearchParams(location.search).get('topic');
		if (!wanted) return;
		const slug = s =>
			String(s)
				.toLowerCase()
				.replace(/[^a-z0-9]+/g, '-')
				.replace(/^-|-$/g, '');
		const target = slug(wanted);
		const btn = $$('.contact-form__type[data-type]').find(
			b => slug(b.dataset.topicName) === target,
		);
		if (btn && !btn.classList.contains('is-active')) btn.click();
	}

	// Featured product gallery (home page "jacket-detail"): clicking a thumbnail just pages through that
	// product's OWN photos (never swaps places with the main photo). Separately, if the section has a
	// "rotate through a collection" set up, the whole PRODUCT - photos, price, the "view" link - swaps to
	// another one from that collection every `data-interval` ms, until the visitor touches a thumbnail
	// themselves (then it stays put on their choice; nothing fights their pick).
	function initFeaturedGallery(root = doc) {
		$$('[data-jacket-gallery]', root).forEach(gallery => {
			if (gallery.dataset.bound) return;
			gallery.dataset.bound = 'true';
			const section = gallery.closest('.jacket-detail') || doc;
			const main = $('[data-jacket-main]', gallery);
			const thumbsList = $('[data-jacket-thumbs]', gallery);
			if (!main || !thumbsList) return;
			const priceEl = $('[data-jacket-price]', section);
			const viewLink = $('[data-jacket-view-link]', section);
			let thumbs = $$('[data-jacket-thumb]', thumbsList);

			let pool = [];
			const poolScript = $('[data-jacket-rotation]', gallery);
			if (poolScript) {
				try {
					pool = JSON.parse(poolScript.textContent) || [];
				} catch (err) {
					pool = [];
				}
			}
			// Preload every photo in the rotation pool (and the current product's own thumbs, since it's
			// pool[0]) so switching never has to wait on the network - that wait was the "lag" on swap.
			pool.forEach(product => {
				product.images.forEach(img => {
					const preload = new Image();
					preload.src = img.full;
				});
			});
			let productIndex = 0;
			let photoTimer = null;

			const selectPhoto = i => {
				const thumb = thumbs[i];
				if (!thumb) return;
				thumbs.forEach(t => t.classList.toggle('is-active', t === thumb));
				main.dataset.full = thumb.dataset.full;
				main.dataset.small = thumb.dataset.small;
				main.dataset.alt = thumb.dataset.alt;
				main.removeAttribute('srcset');
				main.src = thumb.dataset.full;
				main.alt = thumb.dataset.alt || '';
				main.classList.remove('is-switching');
				void main.offsetWidth;
				main.classList.add('is-switching');
			};
			const bindThumbClicks = () => {
				thumbs.forEach((thumb, i) => {
					on(thumb, 'click', () => {
						stop(); // a deliberate choice: don't fight it by jumping to another product underneath the visitor
						selectPhoto(i);
					});
				});
			};
			bindThumbClicks();

			const showProduct = product => {
				thumbsList.innerHTML = product.images
					.map(
						(img, i) => `<li class="jacket-detail__thumb">
							<button type="button" class="jacket-detail__thumb-btn${i === 0 ? ' is-active' : ''}" data-jacket-thumb data-full="${escapeHtml(img.full)}" data-small="${escapeHtml(img.small)}" data-alt="${escapeHtml(img.alt || '')}" aria-label="${escapeHtml((S.showImage || 'Show image __N__').replace('__N__', i + 1))}">
								<img loading="lazy" src="${escapeHtml(img.small)}" alt="${escapeHtml(img.alt || '')}">
							</button>
						</li>`,
					)
					.join('');
				thumbs = $$('[data-jacket-thumb]', thumbsList);
				bindThumbClicks();
				selectPhoto(0);
				if (priceEl) priceEl.innerHTML = product.price;
				if (viewLink) viewLink.setAttribute('href', product.url);
			};

			const stop = () => {
				if (photoTimer) window.clearInterval(photoTimer);
				photoTimer = null;
			};
			const start = () => {
				stop();
				if (pool.length < 2 || reducedMotion()) return; // nothing to rotate through, or the visitor asked for less motion
				const interval = parseInt(gallery.dataset.interval, 10) || 3000;
				photoTimer = window.setInterval(() => {
					productIndex = (productIndex + 1) % pool.length;
					showProduct(pool[productIndex]);
				}, interval);
			};
			start();
		});
	}

	// "Play" button on model cards: an uploaded video file plays in place, a YouTube / Vimeo link opens in an iframe
	doc.addEventListener('click', e => {
		const btn = e.target.closest('[data-video-play]');
		if (!btn) return;
		const media = btn.closest('[data-video-media]');
		if (!media) return;
		const file = $('[data-video-file]', media);
		if (file) {
			const video = $('video', file);
			if (!video) return;
			$$('[data-video-media] video').forEach(other => {
				if (other !== video) other.pause(); // one video at a time
			});
			file.hidden = false;
			btn.hidden = true;
			const started = video.play();
			if (started && started.catch) started.catch(() => {}); // if the browser refuses, the native controls are right there
			return;
		}
		if (!btn.dataset.videoSrc) return;
		const frame = doc.createElement('iframe');
		frame.src = btn.dataset.videoSrc;
		frame.allow = 'autoplay; fullscreen; picture-in-picture';
		frame.allowFullscreen = true;
		frame.title = btn.getAttribute('aria-label') || 'Video';
		frame.className = 'size-models__frame';
		media.appendChild(frame);
		btn.hidden = true;
	});

	// Login page: "forgot password"
	doc.addEventListener('click', e => {
		const toggle = e.target.closest('[data-recover-toggle]');
		if (!toggle) return;
		const recover = $('[data-recover-panel]');
		const login = $('[data-login-panel]');
		if (!recover || !login) return;
		const showRecover = recover.hidden;
		recover.hidden = !showRecover;
		login.hidden = showRecover;
		if (showRecover) history.replaceState(null, '', '#recover');
		else history.replaceState(null, '', location.pathname + location.search);
	});
	ready(() => {
		if (location.hash === '#recover' && $('[data-recover-panel]')) {
			$('[data-recover-panel]').hidden = false;
			const login = $('[data-login-panel]');
			if (login) login.hidden = true;
		}
	});

	// Account addresses
	doc.addEventListener('click', e => {
		const toggle = e.target.closest('[data-address-toggle]');
		if (toggle) {
			const target = doc.getElementById(toggle.dataset.addressToggle);
			if (target) {
				target.hidden = !target.hidden;
				toggle.setAttribute('aria-expanded', String(!target.hidden));
			}
			return;
		}
		const del = e.target.closest('[data-address-delete]');
		if (del && window.confirm(del.dataset.confirmMessage || 'Delete?')) {
			const form = doc.createElement('form');
			form.method = 'post';
			form.action = del.dataset.target;
			const method = doc.createElement('input');
			method.type = 'hidden';
			method.name = '_method';
			method.value = 'delete';
			form.appendChild(method);
			doc.body.appendChild(form);
			form.submit();
		}
	});
	function initAddressSelects(root = doc) {
		$$('[data-address-country]', root).forEach(select => {
			if (select.dataset.bound) return;
			select.dataset.bound = 'true';
			const form = select.closest('form');
			const wrap = $('[data-address-province-wrap]', form);
			const province = $('[data-address-province]', form);
			if (!wrap || !province) return;
			if (select.dataset.default) select.value = select.dataset.default;
			const update = () => {
				const option = select.options[select.selectedIndex];
				let provinces = [];
				try {
					provinces = JSON.parse((option && option.dataset.provinces) || '[]');
				} catch (err) {
					provinces = [];
				}
				province.innerHTML = provinces
					.map(
						([value, label]) =>
							`<option value="${escapeHtml(value)}">${escapeHtml(label)}</option>`,
					)
					.join('');
				wrap.hidden = provinces.length === 0;
				if (provinces.length && province.dataset.default)
					province.value = province.dataset.default;
			};
			on(select, 'change', update);
			update();
		});
	}

	// "More colourways" carousel
	// "More colourways" carousel: slides the track (animated, see .more-colourways__track's transition), rather than
	// just swapping which page is hidden. Off-screen slides get [inert] so they can't be tabbed to or read by AT.
	function initCarousels(root = doc) {
		$$('[data-carousel]', root).forEach(carousel => {
			if (carousel.dataset.bound) return;
			carousel.dataset.bound = 'true';
			const track = $('[data-carousel-track]', carousel);
			const viewport = $('[data-carousel-viewport]', carousel);
			const pages = $$('[data-carousel-page]', carousel);
			const dots = $$('[data-carousel-dot]', carousel);
			const prev = $('[data-carousel-prev]', carousel);
			const next = $('[data-carousel-next]', carousel);
			// all slides need to sit side by side in the track for the slide animation, even the ones rendered `hidden`
			// (a no-JS fallback so only the first slide ever shows without this script)
			pages.forEach(page => {
				page.hidden = false;
			});
			let index = 0;
			const render = () => {
				if (track) track.style.transform = `translateX(-${index * 100}%)`;
				pages.forEach((page, i) => {
					const active = i === index;
					page.toggleAttribute('inert', !active);
					page.setAttribute('aria-hidden', String(!active));
				});
				dots.forEach((dot, i) =>
					dot.classList.toggle('is-active', i === index),
				);
				if (prev)
					prev.classList.toggle('more-colourways__arrow--active', index > 0);
				if (next)
					next.classList.toggle(
						'more-colourways__arrow--active',
						index < pages.length - 1,
					);
			};
			const goPrev = () => {
				index = Math.max(0, index - 1);
				render();
			};
			const goNext = () => {
				index = Math.min(pages.length - 1, index + 1);
				render();
			};
			on(prev, 'click', goPrev);
			on(next, 'click', goNext);
			dots.forEach((dot, i) =>
				on(dot, 'click', () => {
					index = i;
					render();
				}),
			);
			// swipe the slides left/right by touch, same threshold as the product gallery
			let touchStartX = null;
			on(
				viewport,
				'touchstart',
				e => {
					touchStartX = e.changedTouches[0].clientX;
				},
				{ passive: true },
			);
			on(
				viewport,
				'touchend',
				e => {
					if (touchStartX == null) return;
					const dx = e.changedTouches[0].clientX - touchStartX;
					touchStartX = null;
					if (Math.abs(dx) > 50) (dx < 0 ? goNext : goPrev)();
				},
				{ passive: true },
			);
			render();
		});
	}

	/* ---- Lightbox (product zoom) ------------------------------------------- */
	const Lightbox = {
		el: null,
		ui: null,
		state: { images: [], index: 0, trigger: null, onChange: null },
		timer: null,
		isOpen() {
			return !!this.el && this.el.classList.contains('is-open');
		},
		build() {
			const el = doc.createElement('div');
			el.className = 'lightbox';
			el.setAttribute('role', 'dialog');
			el.setAttribute('aria-modal', 'true');
			el.setAttribute('aria-label', 'Image gallery');
			el.setAttribute('aria-hidden', 'true');
			el.hidden = true;
			el.innerHTML = `
        <div class="lightbox__backdrop" data-lightbox-close></div>
        <div class="lightbox__bar">
          <span class="lightbox__counter" aria-live="polite"></span>
          <button type="button" class="lightbox__btn lightbox__close" data-lightbox-close aria-label="Close"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M1.5 1.5L12.5 12.5M12.5 1.5L1.5 12.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg></button>
        </div>
        <div class="lightbox__stage">
          <button type="button" class="lightbox__btn lightbox__nav lightbox__nav--prev" aria-label="Previous"><svg width="8" height="14" viewBox="0 0 8 14" fill="none" aria-hidden="true"><path d="M7 1L1 7L7 13" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
          <figure class="lightbox__figure"><img class="lightbox__image" alt=""></figure>
          <button type="button" class="lightbox__btn lightbox__nav lightbox__nav--next" aria-label="Next"><svg width="8" height="14" viewBox="0 0 8 14" fill="none" aria-hidden="true"><path d="M1 1L7 7L1 13" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
        </div>
        <ul class="lightbox__thumbs list-reset"></ul>`;
			doc.body.appendChild(el);
			this.el = el;
			this.ui = {
				counter: $('.lightbox__counter', el),
				close: $('.lightbox__close', el),
				stage: $('.lightbox__stage', el),
				prev: $('.lightbox__nav--prev', el),
				next: $('.lightbox__nav--next', el),
				image: $('.lightbox__image', el),
				thumbs: $('.lightbox__thumbs', el),
			};
			$$('[data-lightbox-close]', el).forEach(b =>
				on(b, 'click', () => this.close()),
			);
			on(this.ui.prev, 'click', () => this.step(-1));
			on(this.ui.next, 'click', () => this.step(1));
			on(this.ui.thumbs, 'click', e => {
				const t = e.target.closest('[data-lightbox-thumb]');
				if (t) this.show(Number(t.dataset.lightboxThumb));
			});
			on(this.ui.stage, 'click', e => {
				if (e.target === this.ui.stage) this.close();
			});
			let startX = null;
			on(
				this.ui.stage,
				'touchstart',
				e => {
					startX = e.changedTouches[0].clientX;
				},
				{ passive: true },
			);
			on(
				this.ui.stage,
				'touchend',
				e => {
					if (startX == null) return;
					const dx = e.changedTouches[0].clientX - startX;
					startX = null;
					if (Math.abs(dx) > 50) this.step(dx < 0 ? 1 : -1);
				},
				{ passive: true },
			);
			doc.addEventListener('keydown', e => {
				if (!this.isOpen()) return;
				if (e.key === 'Escape') this.close();
				else if (e.key === 'ArrowLeft') this.step(-1);
				else if (e.key === 'ArrowRight') this.step(1);
				else trapFocus(this.el, e);
			});
		},
		show(i) {
			const { images } = this.state;
			this.state.index = Math.min(Math.max(i, 0), images.length - 1);
			const img = images[this.state.index];
			this.ui.image.src = img.src;
			this.ui.image.alt = img.alt || '';
			this.ui.image.classList.remove('is-switching');
			void this.ui.image.offsetWidth;
			this.ui.image.classList.add('is-switching');
			this.ui.counter.textContent = `${this.state.index + 1} / ${images.length}`;
			$$('.lightbox__thumb', this.ui.thumbs).forEach((t, n) => {
				const active = n === this.state.index;
				t.classList.toggle('is-active', active);
				t.setAttribute('aria-current', String(active));
				if (active)
					this.ui.thumbs.scrollLeft =
						t.offsetLeft - (this.ui.thumbs.clientWidth - t.offsetWidth) / 2;
			});
			const single = images.length < 2;
			this.ui.prev.hidden = single;
			this.ui.next.hidden = single;
			this.ui.thumbs.hidden = single;
			this.ui.prev.disabled = this.state.index === 0;
			this.ui.next.disabled = this.state.index === images.length - 1;
			if (this.state.onChange) this.state.onChange(this.state.index);
		},
		step(d) {
			const n = this.state.index + d;
			if (n < 0 || n >= this.state.images.length) return;
			this.show(n);
		},
		open({ images, index = 0, trigger = null, onChange = null }) {
			if (!images || !images.length) return;
			if (!this.el) this.build();
			this.state = { images, index, trigger, onChange };
			this.ui.thumbs.innerHTML = images
				.map(
					(img, i) =>
						`<li><button type="button" class="lightbox__thumb" data-lightbox-thumb="${i}" aria-label="${i + 1}"><img src="${escapeHtml(img.thumb || img.src)}" alt=""></button></li>`,
				)
				.join('');
			this.show(index);
			if (!Drawers.current) lockScroll();
			window.clearTimeout(this.timer);
			this.el.hidden = false;
			void this.el.offsetWidth;
			this.el.classList.add('is-open');
			this.el.setAttribute('aria-hidden', 'false');
			this.ui.close.focus({ preventScroll: true });
		},
		close() {
			if (!this.isOpen()) return;
			this.el.classList.remove('is-open');
			this.el.setAttribute('aria-hidden', 'true');
			this.timer = window.setTimeout(() => {
				this.el.hidden = true;
			}, 250);
			if (!Drawers.current) unlockScroll();
			if (this.state.trigger) this.state.trigger.focus({ preventScroll: true });
		},
	};

	/* ==========================================================================
     5. Cart + promo code
     ========================================================================== */
	const Cart = {
		busy: 0,
		lastRefresh: 0,
		lastJson: null,

		pageSectionId() {
			const page = $('[data-cart-page]');
			return page ? page.dataset.sectionId : '';
		},
		sectionNames() {
			const names = [];
			if ($('[data-cart-drawer]')) names.push('cart-drawer');
			const page = this.pageSectionId();
			if (page) names.push(page);
			return names.join(',');
		},
		setLoading(on_) {
			this.busy += on_ ? 1 : -1;
			const loading = this.busy > 0;
			$$('.drawer--cart, [data-cart-page]').forEach(el =>
				el.classList.toggle('is-loading', loading),
			);
		},
		showError(message) {
			$$('[data-cart-error]').forEach(el => {
				el.textContent = message || '';
				el.hidden = !message;
			});
		},
		async getJson() {
			const res = await fetch(
				`${jsonUrl(routes.cart || '/cart')}?_=${Date.now()}`,
				{ headers: { Accept: 'application/json' } },
			);
			const json = await res.json();
			this.lastJson = json;
			return json;
		},
		/** Runs a cart mutation with the section rendering bundled in, then re-syncs the UI. */
		async mutate(url, body, { promo = true } = {}) {
			this.setLoading(true);
			this.showError('');
			try {
				const sections = this.sectionNames();
				const data = await postJSON(
					url,
					Object.assign(
						{},
						body,
						sections ? { sections, sections_url: location.pathname } : {},
					),
				);
				if (data && data.sections) this.renderSections(data.sections);
				const cart = await this.getJson();
				this.lastRefresh = Date.now();
				this.afterChange(cart, { promo });
				return { data, cart };
			} finally {
				this.setLoading(false);
			}
		},
		add(items) {
			return this.mutate(jsonUrl(routes.cartAdd || '/cart/add'), { items });
		},
		change(key, quantity) {
			return this.mutate(jsonUrl(routes.cartChange || '/cart/change'), {
				id: key,
				quantity,
			});
		},
		update(body, opts) {
			return this.mutate(
				jsonUrl(routes.cartUpdate || '/cart/update'),
				body,
				opts,
			);
		},
		/** Re-fetches the drawer/page markup and the cart JSON. */
		async refresh() {
			const names = this.sectionNames();
			this.setLoading(true);
			try {
				const [cart, sections] = await Promise.all([
					this.getJson(),
					names
						? fetch(
								`${routes.root || '/'}?sections=${encodeURIComponent(names)}`,
								{ headers: { Accept: 'application/json' } },
							)
								.then(r => r.json())
								.catch(() => null)
						: null,
				]);
				if (sections) this.renderSections(sections);
				this.lastRefresh = Date.now();
				this.afterChange(cart);
				return cart;
			} finally {
				this.setLoading(false);
			}
		},
		renderSections(sections) {
			// Drawer: swap the panel contents but keep the element (keeps the slide state and scroll position)
			const drawerHtml = sections['cart-drawer'];
			const drawer = $('[data-cart-drawer]');
			if (drawerHtml && drawer) {
				const fresh = $('.drawer__panel', parseHTML(drawerHtml));
				const panel = $('.drawer__panel', drawer);
				if (fresh && panel) {
					const hadFocusInside = panel.contains(doc.activeElement);
					panel.innerHTML = fresh.innerHTML;
					panel.dataset.itemCount = fresh.dataset.itemCount || '0';
					if (hadFocusInside)
						$('.drawer__close', panel).focus({ preventScroll: true });
				}
			}
			// Cart page
			const pageId = this.pageSectionId();
			const pageHtml = pageId && sections[pageId];
			const page = $('[data-cart-page]');
			if (pageHtml && page) {
				const fresh = $('[data-cart-page]', parseHTML(pageHtml));
				if (fresh) page.innerHTML = fresh.innerHTML;
			}
		},
		afterChange(cart, { promo = true } = {}) {
			const count = cart.item_count || 0;
			$$('[data-cart-badge]').forEach(badge => {
				badge.textContent = count;
				badge.classList.toggle('is-empty', count === 0);
			});
			const drawer = $('[data-cart-drawer]');
			if (drawer) drawer.classList.toggle('is-empty', count === 0);
			if (count === 0) Promo.clearFallback();
			if (promo) {
				Promo.render(cart);
				Promo.enforceSingle(cart);
			}
			emit('cart:updated', { cart });
		},
		/** After adding: open the drawer or go to the cart page. */
		afterAdd() {
			if (config.cartType === 'page') {
				Nav.go(routes.cart || '/cart');
				return;
			}
			const drawer = $('[data-cart-drawer]');
			if (drawer) Drawers.open(drawer, doc.activeElement);
			else Nav.go(routes.cart || '/cart');
		},
	};

	// Quantity buttons / remove inside the cart (drawer + cart page)
	doc.addEventListener('click', async e => {
		const step = e.target.closest('[data-qty-step]');
		const remove = e.target.closest('[data-cart-remove]');
		const item =
			(step || remove) && (step || remove).closest('[data-cart-item]');
		if (!item) return;
		e.preventDefault();
		const key = item.dataset.key;
		const current = parseInt(item.dataset.qty, 10) || 1;
		let quantity = 0;
		if (step) {
			quantity = current + parseInt(step.dataset.qtyStep, 10);
			if (quantity < 1) return;
		}
		try {
			await Cart.change(key, quantity);
		} catch (err) {
			Cart.showError(err.message || S.cartError);
		}
	});

	// Order note on the cart page
	doc.addEventListener('change', e => {
		const note = e.target.closest('[data-cart-note]');
		if (note) Cart.update({ note: note.value }).catch(() => {});
	});

	// Checkout: normally the form posts to /cart and Shopify redirects (the promo code travels with the cart).
	// Only when the store can't store the code in the cart do we hand it over through the URL.
	doc.addEventListener('submit', e => {
		const form = e.target.closest('[data-checkout-form]');
		if (!form) return;
		const code = Promo.fallbackCode();
		if (code) {
			e.preventDefault();
			Nav.go(
				`${routes.root || '/'}checkout?discount=${encodeURIComponent(code)}`,
			);
		}
	});

	// Keep the drawer fresh when it is opened (other tab, expired cart…)
	doc.addEventListener('drawer:open', e => {
		if (e.detail.id === 'cart-drawer' && Date.now() - Cart.lastRefresh > 1500)
			Cart.refresh().catch(() => {});
		if (e.detail.id === 'wishlist-drawer') Wishlist.render();
	});

	/* ---- Promo code: ONE code at a time -------------------------------------
	 * Uses the Cart API `discount` parameter (POST /cart/update.js). The parameter REPLACES the codes on the cart,
	 * and we only ever send a single code, so a second code can never be added. The API answers with
	 * `discount_codes: [{ code, applicable }]`, which we use to show success / error.
	 * If a store answers without `discount_codes` (very old API behaviour) the code is kept in localStorage and
	 * passed to checkout through /checkout?discount=CODE.
	 * -------------------------------------------------------------------------- */
	const Promo = {
		key: 'cc:promo',
		fallbackCode() {
			return store.get(this.key, '') || '';
		},
		clearFallback() {
			store.del(this.key);
		},
		roots() {
			return $$('[data-promo]');
		},
		message(text, kind) {
			this.roots().forEach(root => {
				const el = $('[data-promo-message]', root);
				if (!el) return;
				el.textContent = text || '';
				el.hidden = !text;
				el.classList.toggle('is-error', kind === 'error');
				el.classList.toggle('is-success', kind === 'success');
			});
		},
		setBusy(root, busy) {
			if (!root) return;
			root.classList.toggle('is-busy', busy);
			$$(
				'[data-promo-apply], [data-promo-remove], [data-promo-input]',
				root,
			).forEach(el => {
				el.disabled = busy;
			});
		},
		same(a, b) {
			return (
				String(a || '')
					.trim()
					.toLowerCase() ===
				String(b || '')
					.trim()
					.toLowerCase()
			);
		},
		/** Collapsed by default: only the "+ ADD A DISCOUNT CODE" row; opening it reveals the input + APPLY. */
		setOpen(root, open) {
			if (!root) return;
			const toggle = $('[data-promo-toggle]', root);
			const fields = $('[data-promo-fields]', root);
			if (!toggle || !fields) return;
			toggle.setAttribute('aria-expanded', String(open));
			fields.hidden = !open;
			if (open) {
				const input = $('[data-promo-input]', fields);
				if (input) input.focus({ preventScroll: true });
			}
		},
		async apply(rawCode, root) {
			const code = String(rawCode || '').trim();
			if (!code) {
				this.message(S.promoEmpty, 'error');
				return;
			}
			this.setBusy(root, true);
			this.message('', '');
			try {
				// Sending ONLY this code replaces whatever was on the cart before → never more than one code.
				const { data, cart } = await Cart.update(
					{ discount: code },
					{ promo: false },
				);
				if (!data || !Array.isArray(data.discount_codes)) {
					// The store doesn't report discount codes: remember it and hand it to checkout through the URL.
					store.set(this.key, code);
					this.render(cart);
					this.message(S.promoPendingCheckout, 'success');
					return;
				}
				const hit = data.discount_codes.find(d => this.same(d.code, code));
				if (hit && hit.applicable) {
					store.del(this.key);
					this.render(cart);
					this.message(S.promoApplied.replace('__CODE__', hit.code), 'success');
				} else {
					// Invalid, expired, or not usable with this bag → take it off again and tell the customer.
					const cleared = await Cart.update({ discount: '' }, { promo: false });
					this.render(cleared.cart);
					this.message(S.promoInvalid, 'error');
					this.roots().forEach(r => {
						// the section refresh above collapsed the box back to default → reopen it so the code + error stay visible
						this.setOpen(r, true);
						const input = $('[data-promo-input]', r);
						if (input) input.value = code;
					});
				}
			} catch (err) {
				this.message(err.message || S.cartError, 'error');
			} finally {
				this.setBusy(root, false);
			}
		},
		async remove(root) {
			this.setBusy(root, true);
			try {
				store.del(this.key);
				await Cart.update({ discount: '' });
				this.message(S.promoRemoved, 'success');
			} catch (err) {
				this.message(err.message || S.cartError, 'error');
			} finally {
				this.setBusy(root, false);
			}
		},
		/** Guarantees the single-code rule even if the cart already holds several (e.g. from a discount link). */
		enforceSingle(cart) {
			const codes = (cart && cart.discount_codes) || [];
			if (codes.length <= 1 || this._enforcing) return;
			this._enforcing = true;
			const keep = codes.find(c => c.applicable) || codes[0];
			Cart.update({ discount: keep.code })
				.catch(() => {})
				.finally(() => {
					this._enforcing = false;
				});
		},
		render(cart) {
			const codes = (cart && cart.discount_codes) || [];
			let active = codes.find(c => c.applicable) || codes[0] || null;
			let pending = false;
			if (
				!active &&
				cart &&
				cart.item_count > 0 &&
				this.fallbackCode() &&
				!Array.isArray(cart.discount_codes)
			) {
				active = { code: this.fallbackCode(), applicable: true };
				pending = true;
			}
			this.roots().forEach(root => {
				const applied = $('[data-promo-applied]', root);
				const form = $('[data-promo-form]', root);
				if (!applied || !form) return;
				if (active) {
					applied.hidden = false;
					form.hidden = true;
					$('[data-promo-code]', root).textContent = active.code;
					const chip = $('[data-promo-chip]', root);
					chip.classList.toggle('is-invalid', !active.applicable);
					let note = '';
					if (!active.applicable) note = S.promoNotApplicable;
					else if (cart.total_discount > 0) {
						const percent = cart.original_total_price
							? Math.round(
									(cart.total_discount / cart.original_total_price) * 100,
								)
							: 0;
						note = S.promoSaves
							.replace('__AMOUNT__', formatMoney(cart.total_discount))
							.replace('__PERCENT__', `${percent}%`);
					} else note = S.promoPendingCheckout;
					if (pending) note = S.promoPendingCheckout;
					$('[data-promo-note]', root).textContent = note;
				} else {
					applied.hidden = true;
					form.hidden = false;
				}
			});
		},
	};

	doc.addEventListener('click', e => {
		const toggle = e.target.closest('[data-promo-toggle]');
		if (toggle) {
			const root = toggle.closest('[data-promo]');
			Promo.setOpen(root, toggle.getAttribute('aria-expanded') !== 'true');
			return;
		}
		const apply = e.target.closest('[data-promo-apply]');
		if (apply) {
			const root = apply.closest('[data-promo]');
			Promo.apply($('[data-promo-input]', root).value, root);
			return;
		}
		const remove = e.target.closest('[data-promo-remove]');
		if (remove) Promo.remove(remove.closest('[data-promo]'));
	});
	doc.addEventListener('keydown', e => {
		if (e.key !== 'Enter') return;
		const input = e.target.closest('[data-promo-input]');
		if (!input) return;
		e.preventDefault();
		Promo.apply(input.value, input.closest('[data-promo]'));
	});

	/* ==========================================================================
     6. Wishlist (stored in the visitor's browser)
     ========================================================================== */
	const Wishlist = {
		key: 'cc:wishlist',
		cache: new Map(),
		list() {
			const raw = store.get(this.key, []);
			return Array.isArray(raw) ? raw.filter(h => typeof h === 'string') : [];
		},
		save(list) {
			store.set(this.key, list);
			this.sync();
			emit('wishlist:change', { list });
		},
		has(handle) {
			return this.list().includes(handle);
		},
		add(handle) {
			const list = this.list();
			if (!list.includes(handle)) this.save([handle, ...list]);
		},
		remove(handle) {
			this.save(this.list().filter(h => h !== handle));
		},
		toggle(handle) {
			if (this.has(handle)) {
				this.remove(handle);
				return false;
			}
			this.add(handle);
			return true;
		},
		/** Hearts, header badge, drawer / page. */
		sync() {
			const list = this.list();
			$$('[data-wishlist-toggle]').forEach(btn => {
				const active = list.includes(btn.dataset.productHandle);
				btn.classList.toggle('is-active', active);
				btn.setAttribute('aria-pressed', String(active));
				const label = active ? btn.dataset.labelRemove : btn.dataset.labelAdd;
				if (label && !btn.classList.contains('product-page__save'))
					btn.setAttribute('aria-label', label);
				const text = $('[data-wishlist-text]', btn);
				if (text && btn.dataset.textAdd)
					text.textContent = active
						? btn.dataset.textRemove
						: btn.dataset.textAdd;
			});
			$$('[data-wishlist-badge]').forEach(badge => {
				badge.textContent = list.length;
				badge.classList.toggle('is-empty', list.length === 0);
			});
			const drawer = $('[data-wishlist-drawer]');
			if (
				(drawer && drawer.classList.contains('is-open')) ||
				$('[data-wishlist-grid]')
			)
				this.render();
			else if (drawer) {
				drawer.classList.toggle('is-empty', list.length === 0);
				this.updateCount(list.length);
			}
		},
		updateCount(n) {
			const text = formatCountText(
				n,
				S.wishlistCountOne || '1 item',
				S.wishlistCountOther || '2 items',
			);
			$$('[data-wishlist-count]').forEach(el => {
				el.textContent = text;
			});
		},
		/** Resolves to the product, `false` when it no longer exists (404) or `undefined` on a network problem. */
		async fetchProduct(handle) {
			if (this.cache.has(handle)) return this.cache.get(handle);
			const promise = fetch(
				`${routes.root || '/'}products/${encodeURIComponent(handle)}.js`,
				{ headers: { Accept: 'application/json' } },
			)
				.then(res => {
					if (res.status === 404) return false;
					return res.ok ? res.json() : undefined;
				})
				.catch(() => undefined);
			this.cache.set(handle, promise);
			const result = await promise;
			if (result === undefined) this.cache.delete(handle);
			return result;
		},
		async products() {
			const list = this.list();
			const results = await Promise.all(
				list.map(handle => this.fetchProduct(handle)),
			);
			const found = results.filter(p => p && typeof p === 'object');
			const gone = list.filter((h, i) => results[i] === false);
			// Products that were deleted from the store are dropped quietly (network errors keep the list untouched)
			if (gone.length)
				store.set(
					this.key,
					list.filter(h => !gone.includes(h)),
				);
			return found;
		},
		image(url, width) {
			if (!url) return '';
			return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
		},
		itemHTML(p) {
			const hasVariants = p.variants.length > 1;
			const firstAvailable = p.variants.find(v => v.available) || p.variants[0];
			const soldOut = !p.variants.some(v => v.available);
			const options = hasVariants
				? `<select class="drawer__select" data-wishlist-variant aria-label="${escapeHtml(S.chooseSize)}">${p.variants
						.map(
							v =>
								`<option value="${v.id}"${v.id === firstAvailable.id ? ' selected' : ''}${v.available ? '' : ' disabled'}>${escapeHtml(v.public_title || v.title)}${v.available ? '' : ` — ${escapeHtml(S.soldOut)}`}</option>`,
						)
						.join('')}</select>`
				: '';
			const price =
				p.compare_at_price > p.price
					? `<s class="price__was">${formatMoneyShort(p.compare_at_price)}</s> ${formatMoneyShort(p.price)}`
					: formatMoneyShort(p.price);
			const image = p.featured_image
				? `<img loading="lazy" src="${escapeHtml(this.image(p.featured_image, 240))}" alt="${escapeHtml(p.title)}" width="100" height="100">`
				: '';
			return `
        <li class="drawer__item" data-wishlist-item data-handle="${escapeHtml(p.handle)}">
          <a href="${escapeHtml(p.url)}" class="drawer__thumb" tabindex="-1">${image}</a>
          <div class="drawer__info">
            <h3 class="drawer__name"><a href="${escapeHtml(p.url)}">${escapeHtml(p.title)}</a></h3>
            <p class="drawer__meta">${p.type ? `${escapeHtml(p.type)} · ` : ''}${price}</p>
            <div class="drawer__wish-actions">
              ${options}
              <button type="button" class="drawer__add-btn" data-wishlist-add${soldOut ? ' disabled' : ''}>
                <img src="${escapeHtml(config.cartIcon || '')}" width="14" height="14" alt="">
                <span data-wishlist-add-label>${escapeHtml(soldOut ? S.soldOut : S.addToBag)}</span>
              </button>
            </div>
          </div>
          <div class="drawer__side">
            <button type="button" class="drawer__remove" data-wishlist-remove>${escapeHtml(S.removeItem)}</button>
          </div>
        </li>`;
		},
		cardHTML(p) {
			const image = p.featured_image
				? `<img loading="lazy" src="${escapeHtml(this.image(p.featured_image, 600))}" alt="${escapeHtml(p.title)}" width="600" height="600">`
				: '';
			const soldOut = !p.variants.some(v => v.available);
			const price =
				p.compare_at_price > p.price
					? `<s class="price__was">${formatMoneyShort(p.compare_at_price)}</s> ${formatMoneyShort(p.price)}`
					: `${p.price_varies ? `${escapeHtml(S.priceFrom)} ` : ''}${formatMoneyShort(p.price)}`;
			return `
        <li class="product-card" data-product-handle="${escapeHtml(p.handle)}">
          <button type="button" class="wishlist-btn is-active" data-wishlist-toggle data-product-handle="${escapeHtml(p.handle)}" data-label-add="${escapeHtml(S.wishlistAdd)}" data-label-remove="${escapeHtml(S.wishlistRemove)}" aria-pressed="true" aria-label="${escapeHtml(S.wishlistRemove)}">
            <svg aria-hidden="true" focusable="false" width="16" height="15" viewBox="0 0 16 15" fill="none"><path d="M8 14C8 14 1 9.9 1 5.2C1 2.9 2.8 1.2 5 1.2C6.3 1.2 7.4 1.8 8 2.8C8.6 1.8 9.7 1.2 11 1.2C13.2 1.2 15 2.9 15 5.2C15 9.9 8 14 8 14Z" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
          <a href="${escapeHtml(p.url)}" class="product-card__thumb" tabindex="-1" aria-hidden="true">${soldOut ? `<span class="product-card__stock">${escapeHtml(S.soldOut)}</span>` : ''}${image}</a>
          <h3 class="product-card__name"><a href="${escapeHtml(p.url)}">${escapeHtml(p.title)}</a></h3>
          <p class="product-card__price">${price}</p>
        </li>`;
		},
		async render() {
			const drawer = $('[data-wishlist-drawer]');
			const listEl = $('[data-wishlist-items]');
			const page = $('[data-wishlist-page]');
			const gridEl = $('[data-wishlist-grid]');
			if (!listEl && !gridEl) return;
			const handles = this.list();
			this.updateCount(handles.length);
			if (drawer) drawer.classList.toggle('is-empty', handles.length === 0);
			if (page) {
				const empty = $('[data-wishlist-empty]', page);
				if (empty) empty.hidden = handles.length > 0;
			}
			if (!handles.length) {
				if (listEl) listEl.innerHTML = '';
				if (gridEl) gridEl.innerHTML = '';
				return;
			}
			if (drawer) drawer.classList.add('is-loading');
			const products = await this.products();
			if (drawer) drawer.classList.remove('is-loading');
			const list = this.list();
			const ordered = list
				.map(h => products.find(p => p.handle === h))
				.filter(Boolean);
			this.updateCount(ordered.length);
			if (drawer) drawer.classList.toggle('is-empty', ordered.length === 0);
			if (listEl)
				listEl.innerHTML = ordered.map(p => this.itemHTML(p)).join('');
			if (gridEl) {
				gridEl.innerHTML = ordered.map(p => this.cardHTML(p)).join('');
				const empty = $('[data-wishlist-empty]', page || doc);
				if (empty) empty.hidden = ordered.length > 0;
			}
		},
	};

	doc.addEventListener('click', async e => {
		const toggle = e.target.closest('[data-wishlist-toggle]');
		if (toggle) {
			e.preventDefault();
			const added = Wishlist.toggle(toggle.dataset.productHandle);
			toggle.classList.remove('is-pulse');
			void toggle.offsetWidth;
			if (added) toggle.classList.add('is-pulse');
			// "SAVE FOR LATER" on the product page: no drawer popup, the heart + label switching to "SAVED" is feedback enough
			return;
		}
		const remove = e.target.closest('[data-wishlist-remove]');
		if (remove) {
			const item = remove.closest('[data-wishlist-item]');
			if (item) Wishlist.remove(item.dataset.handle);
			return;
		}
		const add = e.target.closest('[data-wishlist-add]');
		if (add) {
			const item = add.closest('[data-wishlist-item]');
			const select = $('[data-wishlist-variant]', item);
			const product = await Wishlist.fetchProduct(item.dataset.handle);
			if (!product) return;
			const id = select ? Number(select.value) : product.variants[0].id;
			add.disabled = true;
			try {
				await Cart.add([{ id, quantity: 1 }]);
				// No drawer popup / navigation here either: the button turning into "Added" is feedback enough,
				// then the item quietly leaves the wishlist (it's in the bag now).
				const label = $('[data-wishlist-add-label]', add);
				if (label) label.textContent = S.addedToBag;
				window.setTimeout(() => Wishlist.remove(item.dataset.handle), 1200);
			} catch (err) {
				Cart.showError(err.message || S.cartAddError);
				window.alert(err.message || S.cartAddError);
				add.disabled = false;
			}
		}
	});
	window.addEventListener('storage', e => {
		if (e.key === Wishlist.key) Wishlist.sync();
	});

	/* ==========================================================================
     7. Search, collection filters, product page
     ========================================================================== */

	/* ---- Search drawer (predictive search) ---------------------------------
	 * Only the search bar is visible until something is typed. The colour shortcuts, the results and the "see all"
	 * button appear while there is a query, and everything folds away again when the field is emptied or the drawer closes.
	 * With "Show suggestions" switched on in the section, the colour shortcuts + popular products are shown while empty too.
	 * -------------------------------------------------------------------------- */
	const Search = {
		controller: null,
		init(root = doc) {
			const drawer =
				$('[data-search-drawer]', root) || $('[data-search-drawer]');
			if (!drawer || drawer.dataset.bound) return;
			drawer.dataset.bound = 'true';
			const input = $('[data-search-input]', drawer);
			const body = $('[data-search-body]', drawer);
			const grid = $('[data-search-grid]', drawer);
			const empty = $('[data-search-empty]', drawer);
			const label = $('[data-search-results-label]', drawer);
			const all = $('[data-search-all]', drawer);
			const allText = all && $('span', all);
			if (!input || !grid || !body) return;

			const suggestions = drawer.dataset.suggestions === 'true';
			const initialHTML = grid.innerHTML;
			const initialLabel = label
				? label.dataset.defaultLabel || label.textContent
				: '';
			const defaultHref = all ? all.getAttribute('href') : '';
			const defaultAllText = allText ? allText.textContent : '';

			const reset = () => {
				if (this.controller) this.controller.abort();
				grid.innerHTML = suggestions ? initialHTML : '';
				grid.hidden = false;
				if (empty) empty.hidden = true;
				if (label) label.textContent = initialLabel;
				if (all) {
					all.setAttribute('href', defaultHref);
					all.hidden = false;
					if (allText) allText.textContent = defaultAllText;
				}
				$$('[data-search-query]', drawer).forEach(p =>
					p.classList.remove('is-active'),
				);
				body.hidden = !suggestions; // nothing but the bar unless suggestions are enabled
				Wishlist.sync();
			};
			const run = async () => {
				const query = input.value.trim();
				if (!query) {
					reset();
					return;
				}
				if (this.controller) this.controller.abort();
				this.controller = new AbortController();
				const params = new URLSearchParams({
					q: query,
					'resources[type]': 'product',
					'resources[limit]': '8',
					'resources[options][unavailable_products]': 'last',
					'resources[options][fields]': 'title',
					section_id: 'predictive-search',
				});
				try {
					const res = await fetch(
						`${routes.predictiveSearch || '/search/suggest'}?${params}`,
						{ signal: this.controller.signal },
					);
					if (!res.ok) throw new Error('search');
					const html = await res.text();
					const box = $('[data-predictive-results]', parseHTML(html));
					const count = box ? parseInt(box.dataset.count, 10) : 0;
					const ul = box && $('ul', box);
					body.hidden = false; // the colour shortcuts sit above the results, so they show up together with them
					$$('[data-search-query]', drawer).forEach(p =>
						p.classList.toggle(
							'is-active',
							p.dataset.searchQuery.trim().toLowerCase() ===
								query.toLowerCase(),
						),
					);
					grid.innerHTML = ul ? ul.innerHTML : '';
					grid.hidden = count === 0;
					if (empty) empty.hidden = count > 0;
					if (label)
						label.textContent = (S.searchResultsFor || '__TERMS__').replace(
							'__TERMS__',
							query,
						);
					if (all) {
						all.setAttribute(
							'href',
							`${routes.search || '/search'}?type=product&options%5Bfields%5D=title&q=${encodeURIComponent(query)}`,
						);
						all.hidden = count === 0;
						if (allText && S.searchSeeAll) allText.textContent = S.searchSeeAll;
					}
					Wishlist.sync();
				} catch (err) {
					if (err.name !== 'AbortError') reset();
				}
			};
			const debounced = debounce(run, 220);
			on(input, 'input', () => {
				if (!input.value.trim())
					reset(); // fold back immediately when the field is emptied
				else debounced();
			});
			on(drawer, 'click', e => {
				const pill = e.target.closest('[data-search-query]');
				if (!pill) return;
				const active = pill.classList.contains('is-active');
				$$('[data-search-query]', drawer).forEach(p =>
					p.classList.remove('is-active'),
				);
				if (active) {
					input.value = '';
				} else {
					pill.classList.add('is-active');
					input.value = pill.dataset.searchQuery;
				}
				run();
				input.focus();
			});
			// Closing the drawer clears the query (after the slide-up transition), so it always reopens as just the bar
			doc.addEventListener('drawer:close', e => {
				if (e.detail.id !== drawer.id) return;
				window.setTimeout(() => {
					if (drawer.classList.contains('is-open')) return;
					input.value = '';
					reset();
				}, 350);
			});
		},
	};

	/* ---- Collection: dropdown filters, AJAX filtering, "load more" ---------- */
	function initCollection(root = doc) {
		$$('[data-catalog]', root).forEach(section => {
			if (section.dataset.bound) return;
			section.dataset.bound = 'true';
			let openIndex = -1;

			const dropdowns = () => $$('[data-filter]', section);
			const setOpen = (dd, open) => {
				const menu = $('[data-filter-menu]', dd);
				const toggle = $('[data-filter-toggle]', dd);
				if (!menu || !toggle) return;
				if (open) {
					dropdowns().forEach(other => other !== dd && setOpen(other, false));
					menu.hidden = false;
					void menu.offsetWidth;
					menu.classList.add('is-open');
					openIndex = dropdowns().indexOf(dd);
				} else if (!menu.hidden) {
					menu.classList.remove('is-open');
					window.setTimeout(() => {
						if (!menu.classList.contains('is-open')) menu.hidden = true;
					}, 180);
					if (openIndex === dropdowns().indexOf(dd)) openIndex = -1;
				}
				toggle.setAttribute('aria-expanded', String(open));
			};

			on(section, 'click', e => {
				const toggle = e.target.closest('[data-filter-toggle]');
				if (toggle) {
					const dd = toggle.closest('[data-filter]');
					const isOpen = toggle.getAttribute('aria-expanded') === 'true';
					setOpen(dd, !isOpen);
					return;
				}
				// Tag filters (colour dropdowns / chips): the choice is in the URL path, so keep whatever else is set in the form
				const tagLink = e.target.closest('[data-tag-filter]');
				if (tagLink && tagLink.getAttribute('href')) {
					e.preventDefault();
					openIndex = -1; // one colour per group, like the sort radios - close the dropdown once picked (esp. on mobile)
					update(withFormParams(tagLink.getAttribute('href')));
					return;
				}
				const remove = e.target.closest(
					'[data-tag-remove], [data-catalog-clear]',
				);
				if (remove && remove.getAttribute('href')) {
					e.preventDefault();
					update(remove.getAttribute('href'));
					return;
				}
				const more = e.target.closest('[data-load-more]');
				if (more) loadMore(more);
			});
			on(section, 'keydown', e => {
				if (e.key !== 'Escape') return;
				const dd = e.target.closest('[data-filter]');
				if (dd) {
					setOpen(dd, false);
					const toggle = $('[data-filter-toggle]', dd);
					if (toggle) toggle.focus();
				}
			});
			doc.addEventListener('click', e => {
				if (!section.contains(e.target) || !e.target.closest('[data-filter]'))
					dropdowns().forEach(dd => setOpen(dd, false));
			});

			const form = $('[data-catalog-form]', section);
			function withFormParams(href) {
				const u = new URL(href, location.origin);
				new URLSearchParams(new FormData(form)).forEach((v, k) => {
					if (v !== '') u.searchParams.append(k, v);
				});
				return u.pathname + u.search;
			}
			const submit = debounce(() => {
				const params = new URLSearchParams(new FormData(form));
				Array.from(params.keys()).forEach(k => {
					if (params.get(k) === '') params.delete(k);
				});
				const qs = params.toString();
				update(
					`${form.getAttribute('action') || location.pathname}${qs ? `?${qs}` : ''}`,
				);
			}, 250);
			on(form, 'change', e => {
				if (e.target.type === 'radio') openIndex = -1; // sorting is single-choice: close the menu
				submit();
			});
			on(form, 'submit', e => {
				e.preventDefault();
				submit();
			});

			async function fetchSection(url) {
				const u = new URL(url, location.origin);
				u.searchParams.set('section_id', section.dataset.sectionId);
				const res = await fetch(u.toString());
				if (!res.ok) throw new Error('catalog');
				return $('[data-catalog]', parseHTML(await res.text()));
			}
			async function update(url) {
				section.classList.add('is-loading');
				try {
					const fresh = await fetchSection(url);
					if (!fresh) throw new Error('catalog');
					['[data-catalog-toolbar]', '[data-catalog-results]'].forEach(sel => {
						const current = $(sel, section);
						const next = $(sel, fresh);
						if (current && next) current.replaceWith(next);
					});
					// the form itself stays: carry over the new action (it holds the applied tag filters)
					const freshForm = $('[data-catalog-form]', fresh);
					if (freshForm && freshForm.getAttribute('action'))
						form.setAttribute('action', freshForm.getAttribute('action'));
					const u = new URL(url, location.origin);
					u.searchParams.delete('section_id');
					history.pushState({ catalog: true }, '', u.pathname + u.search);
					if (openIndex > -1) {
						const dd = dropdowns()[openIndex];
						if (dd) {
							const menu = $('[data-filter-menu]', dd);
							const toggle = $('[data-filter-toggle]', dd);
							if (menu && toggle) {
								menu.hidden = false;
								menu.classList.add('is-open');
								toggle.setAttribute('aria-expanded', 'true');
							}
						}
					}
					Wishlist.sync();
				} catch (err) {
					Nav.go(url);
				} finally {
					section.classList.remove('is-loading');
				}
			}
			async function loadMore(btn) {
				const url = btn.dataset.nextUrl;
				if (!url) return;
				btn.disabled = true;
				btn.classList.add('is-loading');
				try {
					const fresh = await fetchSection(url);
					const grid = $('[data-catalog-grid]', section);
					const nextGrid = $('[data-catalog-grid]', fresh);
					if (grid && nextGrid)
						Array.from(nextGrid.children).forEach(card =>
							grid.appendChild(card),
						);
					const nextBtn = $('[data-load-more]', fresh);
					if (nextBtn) {
						btn.dataset.nextUrl = nextBtn.dataset.nextUrl;
						btn.disabled = false;
						btn.classList.remove('is-loading');
					} else {
						btn.remove();
					}
					Wishlist.sync();
				} catch (err) {
					btn.disabled = false;
					btn.classList.remove('is-loading');
					Nav.go(url);
				}
			}
			window.addEventListener('popstate', () => {
				if (doc.body.contains(section))
					update(location.pathname + location.search);
			});
		});
	}

	/* ---- Product page ------------------------------------------------------ */
	function initProductPage(root = doc) {
		$$('[data-product-section]', root).forEach(section => {
			if (section.dataset.bound) return;
			section.dataset.bound = 'true';

			/* Gallery */
			let selectGalleryByMedia = () => {};
			const track = $('[data-gallery-track]', section);
			const images = $$('[data-gallery-image]', section);
			const thumbs = $$('[data-gallery-thumb]', section);
			if (images.length && thumbs.length) {
				const prev = $('[data-gallery-prev]', section);
				const next = $('[data-gallery-next]', section);
				const active = () =>
					thumbs.findIndex(t => t.classList.contains('is-active'));
				const select = (i, { scroll = true } = {}) => {
					const thumb = thumbs[i];
					if (!thumb) return;
					thumbs.forEach(t => t.classList.remove('is-active'));
					thumb.classList.add('is-active');
					images.forEach((img, n) => {
						const isActive = n === i;
						img.classList.toggle('is-active', isActive);
						if (isActive) img.removeAttribute('aria-hidden');
						else img.setAttribute('aria-hidden', 'true');
					});
					if (track) track.style.transform = `translateX(-${i * 100}%)`;
					if (scroll && thumb.scrollIntoView)
						thumb.scrollIntoView({
							block: 'nearest',
							inline: 'nearest',
							behavior: reducedMotion() ? 'auto' : 'smooth',
						});
				};
				// wraps around at either end: last -> first, first -> last (infinite loop, no dead ends)
				const step = dir =>
					select((active() + dir + thumbs.length) % thumbs.length);
				thumbs.forEach((t, i) => {
					on(t, 'click', () => select(i));
					on(t, 'keydown', e => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							select(i);
						}
					});
				});
				on(prev, 'click', () => step(-1));
				on(next, 'click', () => step(1));
				select(Math.max(0, active()), { scroll: false });
				selectGalleryByMedia = mediaId => {
					const i = thumbs.findIndex(
						t => String(t.dataset.mediaId) === String(mediaId),
					);
					if (i > -1 && i !== active()) select(i, { scroll: false });
				};
				const zoom = $('[data-gallery-zoom]', section);
				on(zoom, 'click', () => {
					Lightbox.open({
						images: thumbs.map(t => ({
							src: t.dataset.zoom || t.dataset.full,
							thumb: $('img', t) && $('img', t).src,
							alt: t.dataset.alt,
						})),
						index: Math.max(0, active()),
						trigger: zoom,
						onChange: i => select(i, { scroll: false }),
					});
				});
				// Swipe the main photo left/right (same threshold as the lightbox), so the gallery pages by touch too
				const stage = $('.product-page__main', section);
				let touchStartX = null;
				on(
					stage,
					'touchstart',
					e => {
						touchStartX = e.changedTouches[0].clientX;
					},
					{ passive: true },
				);
				on(
					stage,
					'touchend',
					e => {
						if (touchStartX == null) return;
						const dx = e.changedTouches[0].clientX - touchStartX;
						touchStartX = null;
						if (Math.abs(dx) > 50) step(dx < 0 ? 1 : -1);
					},
					{ passive: true },
				);
			} else {
				const zoom = $('[data-gallery-zoom]', section);
				const soloImg = images[0];
				on(zoom, 'click', () => {
					if (soloImg)
						Lightbox.open({
							images: [
								{ src: soloImg.currentSrc || soloImg.src, alt: soloImg.alt },
							],
							trigger: zoom,
						});
				});
			}

			/* Size guide box */
			const sizeToggle = $('[data-size-toggle]', section);
			const sizeBox = $('[data-size-box]', section);
			on(sizeToggle, 'click', () => {
				const open = sizeBox.classList.toggle('is-open');
				sizeToggle.setAttribute('aria-expanded', String(open));
			});

			/* Variants + size rows */
			let product = null;
			try {
				product = JSON.parse($('[data-product-json]', section).textContent);
			} catch (err) {
				product = null;
			}
			const form = $('[data-product-form]', section);
			if (!product || !form) return;
			const rowsWrap = $('[data-size-rows]', section);
			const sizePos = parseInt(rowsWrap.dataset.sizePosition, 10) || 0; // 1-based option position, 0 = none
			const addBtn = $('[data-add-to-bag]', section);
			const addLabel = $('[data-add-to-bag-label]', section);
			const errorEl = $('[data-product-error]', section);
			const priceEl = $('[data-product-price]', section);
			const idInput = $('[data-variant-id]', section);
			const baseLabel = addBtn ? addBtn.dataset.label : '';
			const rowTemplate = $('[data-size-row]', rowsWrap).cloneNode(true);

			/* Colour swatches built from a "Colourway" variant option */
			const colourFieldset = $('[data-colour-fieldset]', section);
			const colourPos = colourFieldset
				? parseInt(colourFieldset.dataset.optionPosition, 10)
				: 0;
			const colourInputs = colourFieldset
				? $$('[data-colour-input]', colourFieldset)
				: [];
			const colourLabel = $('[data-colour-current]', section);
			const variantOptions = v =>
				v.options || [v.option1, v.option2, v.option3].filter(o => o != null);
			const slugify = t =>
				String(t)
					.toLowerCase()
					.replace(/[^a-z0-9]+/g, '-')
					.replace(/^-|-$/g, '');

			const selectedOthers = () => {
				const chosen = {};
				$$('.product-page__option', section).forEach(fs => {
					const checked = $('[data-option-input]:checked', fs);
					if (checked) chosen[fs.dataset.optionPosition] = checked.value;
				});
				return chosen;
			};
			const findVariant = sizeValue => {
				const others = selectedOthers();
				return product.variants.find(v => {
					const opts = variantOptions(v);
					if (sizePos && opts[sizePos - 1] !== sizeValue) return false;
					return Object.keys(others).every(
						pos => opts[Number(pos) - 1] === others[pos],
					);
				});
			};
			const rowVariant = row => {
				const select = $('[data-size-select]', row);
				if (!sizePos) {
					const others = selectedOthers();
					if (!Object.keys(others).length) return product.variants[0];
					return findVariant(undefined);
				}
				return findVariant(select ? select.value : undefined);
			};
			const refreshAvailability = () => {
				$$('[data-size-select]', section).forEach(select => {
					Array.from(select.options).forEach(opt => {
						const v = findVariant(opt.value);
						const available = !!(v && v.available);
						opt.disabled = !available;
						const base = opt.value;
						opt.textContent = available ? base : `${base} — ${S.soldOut}`;
					});
					const current = select.options[select.selectedIndex];
					if (current && current.disabled) {
						const firstOk = Array.from(select.options).find(o => !o.disabled);
						if (firstOk) select.value = firstOk.value;
					}
				});
			};
			const rows = () => $$('[data-size-row]', rowsWrap);
			// Colour swatches with nothing left in stock get a strike-through (they stay selectable)
			const refreshColourAvailability = () => {
				colourInputs.forEach(input => {
					const soldOut = !product.variants.some(
						v =>
							variantOptions(v)[colourPos - 1] === input.value && v.available,
					);
					input
						.closest('.product-swatch')
						.classList.toggle('is-sold-out', soldOut);
				});
			};
			const syncColourUI = () => {
				const checked = colourInputs.find(i => i.checked);
				colourInputs.forEach(i =>
					i.closest('.product-swatch').classList.toggle('is-active', i.checked),
				);
				if (checked && colourLabel) colourLabel.textContent = checked.value;
			};
			let interacted = false;
			const refreshButton = () => {
				let total = 0;
				let anyAvailable = false;
				let firstVariant = null;
				rows().forEach(row => {
					const v = rowVariant(row);
					const qty = parseInt($('[data-qty-value]', row).textContent, 10) || 1;
					if (v && !firstVariant) firstVariant = v;
					if (v && v.available) {
						anyAvailable = true;
						total += v.price * qty;
					}
				});
				if (idInput && firstVariant) idInput.value = firstVariant.id;
				if (firstVariant && colourPos) {
					// show the photo of the chosen colourway and keep ?variant= in the address bar (shareable link)
					if (firstVariant.featured_image && firstVariant.featured_image.id)
						selectGalleryByMedia(firstVariant.featured_image.id);
					if (interacted && window.history && history.replaceState) {
						const url = new URL(window.location.href);
						url.searchParams.set('variant', firstVariant.id);
						url.searchParams.delete('colourway');
						history.replaceState(null, '', url.toString());
					}
				}
				if (priceEl && firstVariant) {
					priceEl.innerHTML =
						(firstVariant.compare_at_price > firstVariant.price
							? `<s class="price__was">${formatMoneyShort(firstVariant.compare_at_price)}</s> `
							: '') + formatMoneyShort(firstVariant.price);
				}
				if (addBtn) {
					addBtn.disabled = !anyAvailable;
					addLabel.textContent = anyAvailable
						? `${baseLabel} — ${formatMoneyShort(total)}`
						: S.soldOut;
				}
			};
			const refreshRemoveButtons = () => {
				const all = rows();
				all.forEach(row => {
					const remove = $('[data-remove-row]', row);
					if (remove) remove.hidden = all.length <= 1;
				});
			};

			on(section, 'click', e => {
				const step = e.target.closest('.product-page__qty [data-qty-step]');
				if (step) {
					const value = $(
						'[data-qty-value]',
						step.closest('.product-page__qty'),
					);
					value.textContent = Math.max(
						1,
						(parseInt(value.textContent, 10) || 1) +
							parseInt(step.dataset.qtyStep, 10),
					);
					refreshButton();
					return;
				}
				if (e.target.closest('[data-add-size-row]')) {
					const row = rowTemplate.cloneNode(true);
					const more = $('[data-add-size-row]', row);
					if (more) more.remove();
					$('[data-qty-value]', row).textContent = '1';
					const remove = doc.createElement('button');
					remove.type = 'button';
					remove.className = 'product-page__row-remove';
					remove.dataset.removeRow = '';
					remove.setAttribute('aria-label', S.removeItem);
					remove.innerHTML = '&times;';
					row.appendChild(remove);
					rowsWrap.appendChild(row);
					// pick the first size that is not used yet
					const used = rows()
						.slice(0, -1)
						.map(
							r =>
								$('[data-size-select]', r) && $('[data-size-select]', r).value,
						);
					const select = $('[data-size-select]', row);
					if (select) {
						const free = Array.from(select.options).find(
							o => !o.disabled && !used.includes(o.value),
						);
						if (free) select.value = free.value;
					}
					refreshAvailability();
					refreshRemoveButtons();
					refreshButton();
					return;
				}
				const del = e.target.closest('[data-remove-row]');
				if (del) {
					del.closest('[data-size-row]').remove();
					refreshRemoveButtons();
					refreshButton();
				}
			});
			on(section, 'change', e => {
				const pill = e.target.closest('[data-option-input]');
				if (pill) {
					interacted = true;
					const label = $(
						'[data-option-current]',
						pill.closest('.product-page__option'),
					);
					if (label) label.textContent = pill.value;
					if (pill.matches('[data-colour-input]')) syncColourUI();
					refreshAvailability();
				}
				if (e.target.closest('[data-size-select]')) interacted = true;
				if (pill || e.target.closest('[data-size-select]')) refreshButton();
			});

			// Deep link to a colourway: /products/the-varsity-jacket?colourway=navy-red (used by the home page swatches)
			if (colourInputs.length) {
				const params = new URLSearchParams(window.location.search);
				const wanted = params.get('colourway');
				if (wanted && !params.get('variant')) {
					const match = colourInputs.find(
						i => slugify(i.value) === slugify(wanted),
					);
					if (match) match.checked = true;
				}
				syncColourUI();
				refreshColourAvailability();
			}
			refreshAvailability();
			refreshRemoveButtons();
			refreshButton();

			/* Add to bag */
			on(form, 'submit', async e => {
				e.preventDefault();
				if (!addBtn || addBtn.disabled) return;
				const merged = new Map();
				rows().forEach(row => {
					const v = rowVariant(row);
					if (!v || !v.available) return;
					const qty = parseInt($('[data-qty-value]', row).textContent, 10) || 1;
					merged.set(v.id, (merged.get(v.id) || 0) + qty);
				});
				const items = Array.from(merged, ([id, quantity]) => ({
					id,
					quantity,
				}));
				if (!items.length) return;
				errorEl.hidden = true;
				addBtn.disabled = true;
				addBtn.classList.add('is-loading');
				try {
					await Cart.add(items);
					// "ADD TO BAG" on the product page: no drawer/page popup, the button turning into "ADDED" is feedback enough
					addLabel.textContent = S.addedToBag;
					window.setTimeout(refreshButton, 1600);
				} catch (err) {
					errorEl.textContent = err.message || S.cartAddError;
					errorEl.hidden = false;
					refreshButton();
				} finally {
					addBtn.classList.remove('is-loading');
				}
			});
		});
	}

	/* ==========================================================================
     8. Scroll animations (AOS) — same behaviour as the original static site
     ========================================================================== */
	function initAnimations() {
		const isMobile = window.matchMedia('(max-width: 768px)').matches;
		if (config.designMode || !window.AOS || reducedMotion() || isMobile) return;
		const MEDIA = [
			'[class*="__media"]',
			'[class*="__image"]',
			'[class*="__gallery"]',
			'.product-page__main',
			'.size-guide__diagram',
		].join(',');
		const LEAD = [
			'.hero__content',
			'.jacket-detail__content',
			'.reviews-intro__content',
			'.our-story__content',
			'.newsletter__content',
			'.text-block__content',
			'.hero-story__content',
			'.about-story__content',
			'.about-secondary-story__content',
			'.product-page__info',
			'.delivery-section__block',
			'.returns-section__block',
			'main [class*="__head"]',
		];
		const GRIDS = [
			'main [class*="__grid"]:not(.colourways__grid) > *',
			'main [class*="__wrapper"] > *',
			'.faq__list > .accordion__item',
			'.returns-steps > li',
			'.info-check > li',
			'.footer__top > *',
		];
		const SINGLE = [
			'.breadcrumbs',
			'.subsection-title',
			'.product-page__main',
			'.product-page__thumbs',
			'.footer__bottom',
		];
		const depth = el => {
			let d = 0;
			for (let n = el; n.parentElement; n = n.parentElement) d += 1;
			return d;
		};
		const tagged = new Map();
		const visible = el => el.getClientRects().length !== 0;
		const insideTagged = el => {
			for (let n = el; n; n = n.parentElement) if (tagged.has(n)) return true;
			return false;
		};
		const containsTagged = el => {
			for (const t of tagged.keys())
				if (t !== el && el.contains(t)) return true;
			return false;
		};
		const tag = (el, delay = 0) => {
			if (!visible(el) || insideTagged(el) || containsTagged(el)) return false;
			tagged.set(el, Math.min(delay, 500));
			return true;
		};
		const many = sels => $$(sels.join(','));
		const tagChildren = els => {
			[...els]
				.sort((a, b) => depth(b) - depth(a))
				.forEach(el => {
					let n = 0;
					Array.from(el.children).forEach(child => {
						if (tag(child, 100 * n)) n += 1;
					});
				});
		};

		tagChildren(many(LEAD));
		const grids = many(GRIDS);
		const tops = grids.map(el => el.offsetTop);
		let n = 0;
		grids.forEach((el, i) => {
			const sameRow =
				i > 0 &&
				el.parentElement === grids[i - 1].parentElement &&
				Math.abs(tops[i] - tops[i - 1]) < 4;
			n = sameRow ? n + 1 : 0;
			tag(el, 100 * n);
		});
		many(SINGLE).forEach(el => tag(el));
		tagChildren(
			$$('main section > .container, main section > .container-mini'),
		);

		const pageHeight = doc.documentElement.scrollHeight;
		Array.from(tagged, ([el, delay]) => ({
			el,
			delay,
			top: el.getBoundingClientRect().top + window.scrollY,
		})).forEach(({ el, delay, top }) => {
			el.setAttribute(
				'data-aos',
				el.matches(MEDIA) ? 'zoom-in-down' : 'fade-down',
			);
			if (delay) el.setAttribute('data-aos-delay', String(delay));
			if (top + 80 >= pageHeight) el.setAttribute('data-aos-offset', '0');
		});
		doc.addEventListener('transitionend', e => {
			const t = e.target;
			if (!(t instanceof HTMLElement) || !t.hasAttribute('data-aos')) return;
			if (e.propertyName !== 'opacity' && e.propertyName !== 'transform')
				return;
			t.removeAttribute('data-aos');
			t.removeAttribute('data-aos-delay');
			t.removeAttribute('data-aos-offset');
			t.classList.remove('aos-init', 'aos-animate');
		});
		void doc.body.offsetHeight;
		window.AOS.init({
			duration: 800,
			easing: 'ease-out-cubic',
			offset: 80,
			once: true,
		});
		window.addEventListener('load', () => window.AOS.refresh());
		if (doc.fonts && doc.fonts.ready)
			doc.fonts.ready.then(() => window.AOS.refresh());
	}

	/* ==========================================================================
     Boot
     ========================================================================== */
	function initAll(root = doc) {
		initHeader();
		initMarquee();
		initCarousels(root);
		initFeaturedGallery(root);
		initCollection(root);
		initProductPage(root);
		initAddressSelects(root);
		Search.init(root);
		Wishlist.sync();
	}

	ready(() => {
		initAll();
		initContactTopicFromURL();
		Wishlist.sync();
		if (
			Wishlist.list().length &&
			($('[data-wishlist-page]') || $('[data-wishlist-grid]'))
		)
			Wishlist.render();
		// Promo state + cart JSON once, only if there is something in the bag
		if (config.cartCount > 0 && ($('[data-promo]') || $('[data-cart-page]')))
			Cart.getJson()
				.then(cart => Cart.afterChange(cart))
				.catch(() => {});
		initAnimations();
	});

	// Theme editor: re-run initialisers for sections that were just re-rendered
	doc.addEventListener('shopify:section:load', e => initAll(e.target));
	doc.addEventListener('shopify:section:unload', () => {});

	// Small public surface, handy for custom code and app integrations
	window.CollegeClassics = { Cart, Wishlist, Promo, Nav, formatMoney };
})();
